import { esFromEn, enFromEs } from "./dictionary";
import type { Locale } from "./index";

/**
 * Runtime DOM translation layer.
 *
 * Components across the app are written with literal strings. Instead of
 * refactoring every one of them, we translate rendered text nodes and common
 * text-bearing attributes in place, using the shared bilingual dictionary.
 *
 * Any string added to the dictionary is translated automatically everywhere.
 */

const ATTRS = ["placeholder", "title", "aria-label", "alt"];
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE"]);

function buildMap(locale: Locale): Map<string, string> {
  const map = new Map<string, string>();
  if (locale === "es") {
    // Spanish-authored strings stay as they are.
    for (const [en, es] of Object.entries(esFromEn)) map.set(en, es);
  } else {
    // Back to English: translate Spanish-authored strings AND revert any text
    // this layer already swapped to Spanish (React does not re-render nodes we
    // mutated in place, so the reverse mapping is what restores English).
    for (const [es, en] of Object.entries(enFromEs)) map.set(es, en);
    for (const [en, es] of Object.entries(esFromEn)) {
      if (!map.has(es)) map.set(es, en);
    }
  }
  return map;
}


/** Count-bearing phrases that cannot be matched literally. */
const PATTERNS: Record<Locale, Array<[RegExp, string]>> = {
  es: [
    [/^(\d+) assessments? completed$/, "$1 evaluaciones completadas"],
    [/^(\d+) of (\d+) completed$/, "$1 de $2 completadas"],
  ],
  en: [
    [/^(\d+) evaluaciones completadas$/, "$1 assessments completed"],
    [/^(\d+) de (\d+) completadas$/, "$1 of $2 completed"],
  ],
};

let patterns: Array<[RegExp, string]> = [];

/** Preserves surrounding whitespace while swapping the meaningful text. */
function translateValue(map: Map<string, string>, raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  let hit = map.get(trimmed);
  if (!hit) {
    for (const [re, replacement] of patterns) {
      if (re.test(trimmed)) {
        hit = trimmed.replace(re, replacement);
        break;
      }
    }
  }
  if (!hit || hit === trimmed) return null;
  const leading = raw.slice(0, raw.indexOf(trimmed[0]));
  const trailing = raw.slice(raw.lastIndexOf(trimmed[trimmed.length - 1]) + 1);
  return `${leading}${hit}${trailing}`;
}


/** Opt out of translation with data-i18n-skip or translate="no". */
function isSkipped(el: Element | null): boolean {
  return !!el?.closest?.("[data-i18n-skip], [translate='no']");
}

function translateNode(map: Map<string, string>, root: Node) {
  if (root.nodeType === Node.TEXT_NODE) {
    if (isSkipped(root.parentElement)) return;
    const next = translateValue(map, root.nodeValue || "");
    if (next !== null) root.nodeValue = next;
    return;
  }
  if (root.nodeType !== Node.ELEMENT_NODE) return;
  if (isSkipped(root as Element)) return;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
    acceptNode: (node) => {
      const parent = node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as Element);
      if (parent && SKIP_TAGS.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
      if (parent && (parent.hasAttribute("data-i18n-skip") || parent.getAttribute("translate") === "no"))
        return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });


  const el = root as Element;
  if (!SKIP_TAGS.has(el.tagName)) applyToElement(map, el);

  let current = walker.nextNode();
  while (current) {
    if (current.nodeType === Node.TEXT_NODE) {
      const next = translateValue(map, current.nodeValue || "");
      if (next !== null) current.nodeValue = next;
    } else {
      applyToElement(map, current as Element);
    }
    current = walker.nextNode();
  }
}

function applyToElement(map: Map<string, string>, el: Element) {
  for (const attr of ATTRS) {
    const value = el.getAttribute(attr);
    if (!value) continue;
    const next = translateValue(map, value);
    if (next !== null) el.setAttribute(attr, next);
  }
  if (el instanceof HTMLInputElement && (el.type === "button" || el.type === "submit")) {
    const next = translateValue(map, el.value);
    if (next !== null) el.value = next;
  }
}

let observer: MutationObserver | null = null;

export function stopDomTranslation() {
  observer?.disconnect();
  observer = null;
}

/**
 * Translates the current document and keeps translating future DOM updates.
 * Returns a cleanup function.
 */
export function startDomTranslation(locale: Locale) {
  stopDomTranslation();
  const map = buildMap(locale);
  patterns = PATTERNS[locale] || [];
  if (map.size === 0) return () => {};

  translateNode(map, document.body);

  observer = new MutationObserver((mutations) => {
    const active = observer;
    if (!active) return;
    active.disconnect();
    for (const mutation of mutations) {
      if (mutation.type === "characterData") {
        translateNode(map, mutation.target);
      } else if (mutation.type === "attributes" && mutation.target instanceof Element) {
        applyToElement(map, mutation.target);
      } else {
        mutation.addedNodes.forEach((node) => translateNode(map, node));
      }
    }
    active.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ATTRS,
    });
  });

  observer.observe(document.body, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ATTRS,
  });

  return stopDomTranslation;
}
